/**
 * WarCarp brand marks (branding/v2), rendered from the SVG templates that
 * branding/build.mjs generates into ../brand/marks.generated.ts.
 *
 * - `logo`: the horizontal lockup — the helmeted carp gripping its rifle, with
 *   the WAR/CARP wordmark set against its face (outlined, so no font needed).
 * - `stacked`: the armed carp over the wordmark (sign-in, splash-like spaces).
 * - `mark`: the armed carp alone.
 * - `fish`: the plain fish used for small icons.
 *
 * Colours come from the theme (`variant`), so one component serves light and
 * dark. Size by `height` (or `width` for the lockups); the other side follows
 * the mark's aspect ratio.
 */

import React, { useId, useMemo } from 'react'
import { SvgXml } from 'react-native-svg'
import { BRAND_COLORS, BRAND_LOGO_TEXT_CENTER, BRAND_MARKS } from '../brand/marks.generated'

export type BrandMarkKind = keyof typeof BRAND_MARKS

export interface BrandMarkProps {
  kind?: BrandMarkKind
  variant?: 'light' | 'dark'
  /** Rendered height in px. Ignored when `width` is set. */
  height?: number
  /** Rendered width in px; the height follows the aspect ratio. */
  width?: number
  /** Overrides the theme colours: the whole mark (fish and wordmark) in one colour. */
  color?: string
  /**
   * `logo` only: shift it so the wordmark (not the whole lockup, whose rifle
   * hangs below) centres on the row it sits in, e.g. beside header buttons.
   */
  centerOnWordmark?: boolean
  /** Leaves the fish out (same size and position), e.g. while the logo easter egg animates it elsewhere. */
  hideFish?: boolean
  testID?: string
}

/**
 * The mark's SVG in theme colours. `idPrefix` makes its mask ids unique: two
 * instances sharing ids resolve to whichever comes first in the page, and if
 * that copy is hidden (display:none) browsers draw no cutouts at all.
 */
export function brandMarkXml(kind: BrandMarkKind, variant: 'light' | 'dark', color?: string, idPrefix?: string, hideFish = false): string {
  const c = BRAND_COLORS[variant]
  const svg = idPrefix ? BRAND_MARKS[kind].svg.replace(/wc-(logo|stacked|mark|fish)-/g, `${idPrefix}-$1-`) : BRAND_MARKS[kind].svg
  return svg
    .replace(/\{\{FISH\}\}/g, hideFish ? 'none' : color ?? c.fish)
    .replace(/\{\{WAR\}\}/g, color ?? c.war)
    .replace(/\{\{CARP\}\}/g, color ?? c.carp)
}

export function BrandMark({ kind = 'logo', variant = 'light', height = 40, width, color, centerOnWordmark = false, hideFish = false, testID }: BrandMarkProps) {
  const { aspect } = BRAND_MARKS[kind]
  const w = width ?? height * aspect
  const h = width ? width / aspect : height
  // useId is unique per instance; strip characters that aren't valid in url(#id) references.
  const uid = `wc${useId().replace(/[^a-zA-Z0-9]/g, '')}`
  const xml = useMemo(() => brandMarkXml(kind, variant, color, uid, hideFish), [kind, variant, color, uid, hideFish])
  return (
    <SvgXml
      xml={xml}
      width={w}
      height={h}
      style={centerOnWordmark && kind === 'logo' ? { transform: [{ translateY: (0.5 - BRAND_LOGO_TEXT_CENTER) * h }] } : undefined}
      testID={testID}
      // No `accessible` prop: react-native-svg forwards it to the web <svg> as an invalid attribute.
      accessibilityRole="image"
      accessibilityLabel={kind === 'fish' || kind === 'mark' ? 'WarCarp carp' : 'WarCarp'}
    />
  )
}
